import axios from 'axios';
import { API } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// Logistics Payload Interfaces
export interface LogisticsPayload {
  Project_Key: string;
  Freight_Type_Key: string;
  Pallet_Specifications: string;
  Shipment_Requirement_Key: string;
  Design_To_Distribute_Complete: string;
  Wh_Requirements: string;
  Comment: string;
  Risk_Key: string;
}

export interface LogisticsPayloadResponse {
  Logistics_Id: number;
  Project_Key: number;
  Project_Name: string;
  Freight_Type_Key: number;
  Shipment_Requirement_Key: number;
  Freight_Type: string;
  Shipment_Requirement: string;
  Pallet_Specifications: number;
  Design_To_Distribute_Complete: number;
  Wh_Requirements: number;
  Comment: string;
  Risk_Key: number;
  Risk_Name: string;
}

// Logistics API Client
export const useApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token);
  });

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken ? `Bearer ${idToken}` : `Bearer ${authData.idToken}`,
      },
    });

     // Add response interceptor to handle 401 errors
     instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const {token}  = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    
    return instance;
  }, [idToken, authData.idToken]);

  // Lookup APIs
  const getLSFreight = async () => {
    try {
      const response = await apiClient.get('/lookup/LS_Freight');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching LS Freight');
    }
  };

  const getLSPalletSpecifications = async () => {
    try {
      const response = await apiClient.get('/lookup/LS_Pallet_Specifications');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching LS Pallet Specifications');
    }
  };

  const getLSShipmentRequirement = async () => {
    try {
      const response = await apiClient.get('/lookup/LS_Shipment_Requirement');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching LS Shipment Requirement');
    }
  };
const getDesignToDistributeComplete = async () => {
    try {
      const response = await apiClient.get('/lookup/LS_Design_to_Distribute_Complete');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Design To Distribute Complete options');
    }
  };
  const getWhRequirements = async () => {
    try {
      const response = await apiClient.get('/lookup/LS_WH_Requirements');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching WH Requirements options');
    }
  };
  // Helper functions
  // const getDesignToDistributeComplete = () => [
  //   { key: 1, value: 'Yes' },
  //   { key: 2, value: 'No' },
  //   { key: 3, value: 'Not Applicable' },
  // ];

  // const getWhRequirements = () => [
  //   { key: 1, value: 'Yes' },
  //   { key: 2, value: 'No' },
  // ];

  // Risk API
  const getLogisticsRisk = async () => {
    try {
      const response = await apiClient.get('/risks');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching logistics risks');
    }
  };

  // Logistics APIs
  const getLogistics = async (project_key: string) => {
    try {
      const response = await apiClient.get(`/logistics/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching logistics');
    }
  };

  const createLogistics = async (payload: LogisticsPayload) => {
    try {
      const response = await apiClient.post<LogisticsPayloadResponse>('/logistics', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating logistics');
    }
  };

  const updateLogistics = async (payload: LogisticsPayload, project_key: string) => {
    try {
      const response = await apiClient.put<LogisticsPayloadResponse>(`/logistics/${project_key}`, payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'updating logistics');
    }
  };

  return {
    getLSFreight,
    getLSPalletSpecifications,
    getLSShipmentRequirement,
    getDesignToDistributeComplete,
    getWhRequirements,
    getLogisticsRisk,
    getLogistics,
    createLogistics,
    updateLogistics,
  };
};